import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from './Toast';
import api from '../services/api';
import ActivateServicesModal from './ActivateServicesModal';

const SignedProposalModal = () => {
  const [proposals, setProposals] = useState([]);
  const [screen, setScreen] = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [clientForm, setClientForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState([]);
  const [activateProposal, setActivateProposal] = useState(null);
  const [uploading, setUploading] = useState(false);
  const processingRef = useRef(false); // true = user is in configure/save/activate flow

  // fetchPending: ONLY fetches data. Never changes screen if processingRef=true
  const fetchPending = useCallback(async (openList = false) => {
    try {
      const r = await api.get('/service-agreements/pending-signed');
      const data = (r.data || []).filter(p => !dismissed.includes(p.id));
      if (processingRef.current) return; // processing: update nothing
      setProposals(data);
      if (openList && data.length > 0) setScreen('list');
      else if (data.length === 0) setScreen(null);
    } catch (e) {}
  }, [dismissed]);

  useEffect(() => {
    fetchPending(true);
    const token = localStorage.getItem('token') || '';
    let sse = null;
    if (token) {
      try {
        sse = new EventSource('https://api.safecorepest.com/api/events?token=' + encodeURIComponent(token));
        sse.addEventListener('proposal_signed', () => fetchPending(true));
        sse.onerror = () => {};
      } catch (e) {}
    }
    const onBadgeClick = () => { if (!processingRef.current) fetchPending(true); };
    window.addEventListener('sc:open_signed_modal', onBadgeClick);
    return () => {
      if (sse) sse.close();
      window.removeEventListener('sc:open_signed_modal', onBadgeClick);
    };
  }, [fetchPending]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : 'N/A';

  const handleConfigure = (p) => {
    processingRef.current = true; // LOCK - no fetchPending will touch state
    setSelectedProposal(p);
    setClientForm({
      name: p.client_name || '',
      industry: p.industry_type || '',
      email: p.signer_email || p.client_email || '',
      phone: p.signer_phone || '',
      billing_address: p.billing_address || p.facility_address || '',
      billing_city: p.facility_city || '',
      billing_state: p.facility_state || '',
      billing_zip: p.facility_zip || '',
      contract_start_date: p.start_date ? p.start_date.substring(0, 10) : '',
      facility_name: p.facility_name || '',
      facility_address: p.facility_address || '',
      facility_city: p.facility_city || '',
      facility_state: p.facility_state || '',
      facility_zip: p.facility_zip || '',
      site_contact_name: p.site_contact_name || '',
      site_contact_phone: p.site_contact_phone || '',
      certification_scheme: p.certification_scheme || '',
      service_type: p.service_type || '',
      frequency: p.frequency || '',
      price_per_service: p.price_per_service || '',
    });
    setScreen('form');
    // Note: no DB lock needed — processingRef prevents UI state changes
  };

  const handleSave = async () => {
    if (!clientForm.name) { toast.error('Client name is required'); return; }
    setSaving(true);
    try {
      // 1. Create client
      const cRes = await api.post('/clients', {
        name: clientForm.name, industry: clientForm.industry,
        email: clientForm.email, phone: clientForm.phone,
        billing_address: clientForm.billing_address, billing_city: clientForm.billing_city,
        billing_state: clientForm.billing_state, billing_zip: clientForm.billing_zip,
        contract_start_date: clientForm.contract_start_date || null,
      });
      const clientId = (cRes.data.data || cRes.data).id;

      // 2. Create facility
      let facilityId = null;
      if (clientForm.facility_name) {
        const fRes = await api.post('/facilities', {
          client_id: clientId, name: clientForm.facility_name,
          address: clientForm.facility_address, city: clientForm.facility_city,
          state: clientForm.facility_state, zip_code: clientForm.facility_zip,
          site_contact_name: clientForm.site_contact_name,
          site_contact_phone: clientForm.site_contact_phone,
          certification_scheme: clientForm.certification_scheme,
        });
        facilityId = (fRes.data.data || fRes.data).id;
      }

      // 3. Link agreement → active
      await api.put('/service-agreements/' + selectedProposal.id, {
        client_id: clientId, facility_id: facilityId, status: 'active'
      });

      // 4. PDF auto-attached by backend when agreement is linked

      // 5. Remove processed proposal from local list
      const remaining = proposals.filter(p => p.id !== selectedProposal.id);
      setProposals(remaining);

      // 6. Go to activate or done
      if (selectedProposal.frequency && selectedProposal.frequency !== 'one_time' && facilityId) {
        setActivateProposal({ ...selectedProposal, facility_id: facilityId });
        setScreen('activate'); // SWITCH: main modal hidden, activate modal shown
      } else {
        finishProcessing(remaining);
        toast.success('Client "' + clientForm.name + '" created successfully!');
      }
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Error: ' + (e.response?.data?.message || e.message));
      finishProcessing(proposals);
    }
    setSaving(false);
  };

  const finishProcessing = (remainingProposals) => {
    processingRef.current = false; // UNLOCK
    if (remainingProposals.length > 0) {
      setScreen('list');
    } else {
      setScreen(null);
      setProposals([]);
    }
  };

  const downloadPdf = async (id) => {
    try {
      const r = await api.get('/service-agreements/' + id + '/signed-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'SP-' + String(id).padStart(4,'0') + '_Signed.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error downloading PDF'); }
  };

  const dismiss = (id) => {
    setDismissed(prev => [...prev, id]);
    const rem = proposals.filter(p => p.id !== id);
    setProposals(rem);
    if (rem.length === 0) setScreen(null);
  };

  // SCREEN: activate → ONLY show ActivateServicesModal
  if (screen === 'activate' && activateProposal) {
    return (
      <ActivateServicesModal
        proposal={activateProposal}
        onSuccess={async (result) => {
          const rem = proposals;
          setActivateProposal(null);
          finishProcessing(rem);
          toast.success(result.message || 'Services activated successfully!');
        }}
        onSkip={async () => {
          const rem = proposals;
          setActivateProposal(null);
          finishProcessing(rem);
          toast.success('Client created. Activate services later from the client facility.');
        }}
      />
    );
  }

  // SCREEN: null → render nothing
  if (screen !== 'list' && screen !== 'form') return null;

  const s = {
    overlay: { position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center' },
    modal: { background:'white',borderRadius:'12px',maxWidth:'700px',width:'95%',maxHeight:'90vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)' },
    header: { background:'linear-gradient(135deg,#2D5C3F,#1A3A2A)',padding:'20px 24px',borderRadius:'12px 12px 0 0',display:'flex',alignItems:'center',justifyContent:'space-between' },
    badge: { background:'#FF9800',color:'white',borderRadius:'50%',width:'28px',height:'28px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',fontWeight:'700' },
    card: { border:'1px solid #E0E0E0',borderRadius:'8px',padding:'16px',margin:'0 0 12px',background:'#FAFAFA' },
    btn: { padding:'8px 16px',borderRadius:'6px',fontSize:'13px',fontWeight:'600',cursor:'pointer',border:'none' },
    input: { width:'100%',padding:'8px 10px',borderRadius:'6px',border:'1px solid #DDD',fontSize:'13px',boxSizing:'border-box',marginBottom:'8px' },
    label: { fontSize:'10px',color:'#888',textTransform:'uppercase',fontWeight:'700',letterSpacing:'0.5px',marginBottom:'2px',display:'block' },
    grid2: { display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px' },
  };

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget && !processingRef.current) setScreen(null); }}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <div style={s.badge}>{proposals.length}</div>
            <div>
              <div style={{color:'white',fontSize:'16px',fontWeight:'700'}}>Signed Proposals Pending Setup</div>
              <div style={{color:'rgba(255,255,255,0.6)',fontSize:'12px'}}>Configure clients to activate agreements</div>
            </div>
          </div>
          <button onClick={() => { if (!processingRef.current) setScreen(null); }} style={{background:'none',border:'none',color:'rgba(255,255,255,0.7)',fontSize:'20px',cursor:'pointer'}}>&times;</button>
        </div>

        <div style={{padding:'20px 24px'}}>
          {screen === 'list' ? (
            <>
              {proposals.map(p => (
                <div key={p.id} style={s.card}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'10px'}}>
                    <div>
                      <div style={{fontSize:'15px',fontWeight:'700',color:'#1A3A2A'}}>{p.client_name}</div>
                      <div style={{fontSize:'12px',color:'#888'}}>SP-{String(p.id).padStart(4,'0')} &bull; {p.service_type} &bull; {p.frequency}</div>
                    </div>
                    <span style={{padding:'3px 10px',borderRadius:'12px',fontSize:'11px',fontWeight:'600',background:p.sign_method==='online'?'#E8F5E9':'#FFF3E0',color:p.sign_method==='online'?'#2E7D32':'#E65100'}}>
                      {p.sign_method === 'online' ? '\u2713 Signed Online' : '\uD83D\uDCE4 Scanned'}
                    </span>
                  </div>
                  <div style={{fontSize:'12px',color:'#666',marginBottom:'10px'}}>
                    Signed by: <strong>{p.signer_name||'N/A'}</strong>{p.signer_title?' ('+p.signer_title+')':''} &bull; {fmt(p.signed_at)}
                  </div>
                  <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                    <button onClick={() => handleConfigure(p)} style={{...s.btn,background:'#2D5C3F',color:'white'}}>Configure Client</button>
                    <button onClick={() => downloadPdf(p.id)} style={{...s.btn,background:'#E3F2FD',color:'#1565C0'}}>Download PDF</button>
                    <button onClick={() => dismiss(p.id)} style={{...s.btn,background:'#F5F5F5',color:'#888'}}>Dismiss</button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
                <div style={{fontSize:'16px',fontWeight:'700',color:'#1A3A2A'}}>Configure Client from SP-{String(selectedProposal?.id).padStart(4,'0')}</div>
                <button onClick={() => { finishProcessing(proposals); setScreen('list'); }} style={{...s.btn,background:'#F5F5F5',color:'#666'}}>Back</button>
              </div>

              <div style={{background:'#F0F7F4',borderRadius:'8px',padding:'16px',marginBottom:'16px'}}>
                <div style={{fontSize:'13px',fontWeight:'700',color:'#2D5C3F',marginBottom:'10px'}}>Client Information</div>
                <div style={s.grid2}>
                  <div><div style={s.label}>Company Name *</div><input style={s.input} value={clientForm.name||''} onChange={e=>setClientForm({...clientForm,name:e.target.value})} /></div>
                  <div><div style={s.label}>Industry</div><input style={s.input} value={clientForm.industry||''} onChange={e=>setClientForm({...clientForm,industry:e.target.value})} /></div>
                  <div><div style={s.label}>Email</div><input style={s.input} value={clientForm.email||''} onChange={e=>setClientForm({...clientForm,email:e.target.value})} /></div>
                  <div><div style={s.label}>Phone</div><input style={s.input} value={clientForm.phone||''} onChange={e=>setClientForm({...clientForm,phone:e.target.value})} /></div>
                  <div><div style={s.label}>Billing Address</div><input style={s.input} value={clientForm.billing_address||''} onChange={e=>setClientForm({...clientForm,billing_address:e.target.value})} /></div>
                  <div><div style={s.label}>City</div><input style={s.input} value={clientForm.billing_city||''} onChange={e=>setClientForm({...clientForm,billing_city:e.target.value})} /></div>
                  <div><div style={s.label}>State</div><input style={s.input} value={clientForm.billing_state||''} onChange={e=>setClientForm({...clientForm,billing_state:e.target.value})} /></div>
                  <div><div style={s.label}>ZIP</div><input style={s.input} value={clientForm.billing_zip||''} onChange={e=>setClientForm({...clientForm,billing_zip:e.target.value})} /></div>
                  <div><div style={s.label}>Contract Signed Date</div><input type="date" style={s.input} value={clientForm.contract_start_date||''} onChange={e=>setClientForm({...clientForm,contract_start_date:e.target.value})} /></div>
                </div>
              </div>

              {clientForm.facility_name && (
                <div style={{background:'#E3F2FD',borderRadius:'8px',padding:'16px',marginBottom:'16px'}}>
                  <div style={{fontSize:'13px',fontWeight:'700',color:'#1565C0',marginBottom:'10px'}}>Facility (auto-created)</div>
                  <div style={s.grid2}>
                    <div><div style={s.label}>Facility Name</div><input style={s.input} value={clientForm.facility_name||''} onChange={e=>setClientForm({...clientForm,facility_name:e.target.value})} /></div>
                    <div><div style={s.label}>Address</div><input style={s.input} value={clientForm.facility_address||''} onChange={e=>setClientForm({...clientForm,facility_address:e.target.value})} /></div>
                    <div><div style={s.label}>City</div><input style={s.input} value={clientForm.facility_city||''} onChange={e=>setClientForm({...clientForm,facility_city:e.target.value})} /></div>
                    <div><div style={s.label}>State</div><input style={s.input} value={clientForm.facility_state||''} onChange={e=>setClientForm({...clientForm,facility_state:e.target.value})} /></div>
                    <div><div style={s.label}>ZIP</div><input style={s.input} value={clientForm.facility_zip||''} onChange={e=>setClientForm({...clientForm,facility_zip:e.target.value})} /></div>
                    <div><div style={s.label}>Site Contact</div><input style={s.input} value={clientForm.site_contact_name||''} onChange={e=>setClientForm({...clientForm,site_contact_name:e.target.value})} /></div>
                  </div>
                </div>
              )}

              <div style={{background:'#FFF8E1',borderRadius:'8px',padding:'16px',marginBottom:'16px'}}>
                <div style={{fontSize:'13px',fontWeight:'700',color:'#F57F17',marginBottom:'6px'}}>Service Agreement Details</div>
                <div style={{fontSize:'12px',color:'#666'}}>
                  {clientForm.service_type} &bull; {clientForm.frequency} &bull; ${parseFloat(clientForm.price_per_service||0).toFixed(2)}/visit
                </div>
              </div>

              <button onClick={handleSave} disabled={saving} style={{...s.btn,background:'#2D5C3F',color:'white',width:'100%',padding:'12px',fontSize:'15px',opacity:saving?0.6:1}}>
                {saving ? 'Creating...' : 'Create Client & Activate Agreement'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignedProposalModal;
